import { i18n } from '../i18n.js';
import { MoonfallErrorRingBuffer } from '../utils/error-ring-buffer.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * UI Feedback Module — structured survey form.
 *
 * Required: feedback type chip + overall rating (1..5 emoji).
 * Optional: per-feature ratings grid, "fix one thing first" priority chip,
 * NPS 0..10 chip strip, ≤500-char comment, follow-up email checkbox.
 *
 * Auto-captures page route, viewport, user agent, language, recent console errors,
 * and any caller-supplied gameContext. Submits to convex/feedback.ts:submitFeedback.
 */

import { Confirm } from './confirm-dialog.js';
import { DiscordLinks } from './discord-links.js';
import { Firebase } from '../firebase-loader.js';
import { getCloudAchievementBannerConsumer } from '../app-services.js';
import { Lobby } from './lobby.js';
let Feedback = {};

Feedback = (function () {
  const TYPES = [
    { id: 'bug', icon: '🐛', labelKey: 'feedback.type.bug' },
    { id: 'idea', icon: '💡', labelKey: 'feedback.type.idea' },
    { id: 'praise', icon: '❤️', labelKey: 'feedback.type.praise' },
    { id: 'complaint', icon: '😡', labelKey: 'feedback.type.complaint' },
    { id: 'other', icon: '💬', labelKey: 'feedback.type.other' },
  ];

  const OVERALL_RATINGS = [
    { id: 1, icon: '😡' },
    { id: 2, icon: '😕' },
    { id: 3, icon: '😐' },
    { id: 4, icon: '🙂' },
    { id: 5, icon: '🤩' },
  ];

  const FEATURES = [
    { id: 'pacing', labelKey: 'feedback.feature.pacing' },
    { id: 'cards', labelKey: 'feedback.feature.cards' },
    { id: 'story', labelKey: 'feedback.feature.story' },
    { id: 'visuals', labelKey: 'feedback.feature.visuals' },
    { id: 'audio', labelKey: 'feedback.feature.audio' },
    { id: 'tutorial', labelKey: 'feedback.feature.tutorial' },
    { id: 'foundry', labelKey: 'feedback.feature.foundry' },
    { id: 'moonDream', labelKey: 'feedback.feature.moonDream' },
    { id: 'deckBuilder', labelKey: 'feedback.feature.deckBuilder' },
    { id: 'performance', labelKey: 'feedback.feature.performance' },
  ];

  const PRIORITIES = [
    { id: 'pacing', labelKey: 'feedback.priority.pacing' },
    { id: 'cards', labelKey: 'feedback.priority.cards' },
    { id: 'story', labelKey: 'feedback.priority.story' },
    { id: 'visuals', labelKey: 'feedback.priority.visuals' },
    { id: 'audio', labelKey: 'feedback.priority.audio' },
    { id: 'tutorial', labelKey: 'feedback.priority.tutorial' },
    { id: 'foundry', labelKey: 'feedback.priority.foundry' },
    { id: 'moonDream', labelKey: 'feedback.priority.moonDream' },
    { id: 'deckBuilder', labelKey: 'feedback.priority.deckBuilder' },
    { id: 'performance', labelKey: 'feedback.priority.performance' },
    { id: 'newFeatures', labelKey: 'feedback.priority.newFeatures' },
    { id: 'mobile', labelKey: 'feedback.priority.mobile' },
    { id: 'other', labelKey: 'feedback.priority.other' },
  ];

  const MESSAGE_MAX_LENGTH = 500;
  // Manual screenshot attachments — users pick images themselves (no auto capture).
  const MAX_SCREENSHOT_COUNT = 3;
  const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024; // 5 MB
  const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
  const SURFACE_OWNER = 'feedback';

  let overlay = null;
  let state = null;
  let onKeyDown = null;
  let pendingReturnFocus = null;
  let pendingCloseOwner = null;
  let surfaceGeneration = 0;
  let focusTimer = null;

  function claimLobbySurface() {
    const owner = document.body?.dataset?.lobbySurfaceOwner;
    if (owner && owner !== SURFACE_OWNER) return false;
    if (document.body) document.body.dataset.lobbySurfaceOwner = SURFACE_OWNER;
    return true;
  }

  function releaseLobbySurface() {
    if (document.body?.dataset?.lobbySurfaceOwner === SURFACE_OWNER) {
      delete document.body.dataset.lobbySurfaceOwner;
    }
  }

  function uiText(key, fallback, replacements) {
    const translated = i18n && i18n.t ? i18n.t(key, replacements || {}) : null;
    /* The FALLBACK is interpolated too. It is returned verbatim whenever
       i18n has not resolved — the normal case on a cold load — and the
       ui.json wording these fallbacks mirror carries `{{name}}`
       placeholders, so a user could be shown a literal `{{count}}`. Both
       brace styles are handled because the copy uses both. */
    const source = translated && translated !== key ? translated : fallback;
    return Object.entries(replacements || {}).reduce(
        (text, [name, value]) => String(text)
            .split(`{{${name}}}`).join(String(value))
            .split(`{${name}}`).join(String(value)),
        String(source == null ? key : source),
    );
  }

  function el(tag, attrs, children) {
    const node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach((k) => {
        const v = attrs[k];
        if (v == null || v === false) return;
        if (k === 'className') node.className = v;
        else if (k === 'textContent') node.textContent = v;
        else if (k === 'disabled') node.disabled = !!v;
        else if (k === 'htmlFor') node.htmlFor = v;
        else if (k.startsWith('on') && typeof v === 'function') {
          node.addEventListener(k.slice(2).toLowerCase(), v);
        } else if (k === 'value') {
          node.value = v;
        } else {
          node.setAttribute(k, v);
        }
      });
    }
    (children || []).forEach((child) => {
      if (child == null || child === false) return;
      if (typeof child === 'string') node.appendChild(document.createTextNode(child));
      else node.appendChild(child);
    });
    return node;
  }

  /**
   * The subtitle's "Discord" is the door to #feedback (ui/discord-links.js).
   * The sentence stays the translator's: every active language names Discord
   * as the word itself, and one that ever doesn't keeps its plain sentence.
   */
  function buildSubtitle() {
    const sentence = uiText('feedback.modalSubtitle', 'Help us make Moonfall better. Long thoughts also welcome on Discord.');
    const subtitle = el('p', { id: 'feedback-subtitle', className: 'feedback-subtitle' });
    const at = sentence.indexOf('Discord');
    const link = at === -1 ? null : DiscordLinks.createLink('feedback', 'Discord', 'feedback-subtitle-link');
    if (!link) {
      subtitle.textContent = sentence;
      return subtitle;
    }
    subtitle.append(sentence.slice(0, at), link, sentence.slice(at + 'Discord'.length));
    return subtitle;
  }

  function isOpen() {
    return !!overlay;
  }

  function getPageRoute() {
    try {
      if (document.body && document.body.classList && document.body.classList.contains('game-active')) return 'game';
      if (document.body && document.body.classList && document.body.classList.contains('foundry-surface-active')) return 'foundry';
      const lobby = document.getElementById('lobby-view');
      if (lobby && lobby.style.display !== 'none' && !lobby.classList.contains('hidden')) return 'lobby';
    } catch (_) {}
    return 'unknown';
  }

  function getViewport() {
    try {
      return { w: Math.floor(window.innerWidth || 0), h: Math.floor(window.innerHeight || 0) };
    } catch (_) { return undefined; }
  }

  function getLanguage() {
    try {
      if (i18n && typeof i18n.getLanguage === 'function') return i18n.getLanguage();
      if (i18n && i18n.language) return i18n.language;
      return (navigator && navigator.language) || 'en';
    } catch (_) { return 'en'; }
  }

  function getConsoleErrorsBlob() {
    try {
      if (MoonfallErrorRingBuffer && typeof MoonfallErrorRingBuffer.snapshotJson === 'function') {
        return MoonfallErrorRingBuffer.snapshotJson();
      }
    } catch (_) {}
    return undefined;
  }

  // Best-effort pull of the caller's recent SERVER-side errors, attached to the report.
  // Never throws and never blocks submission — returns undefined on any hiccup.
  async function getRecentServerErrors() {
    try {
      if (!Firebase || typeof Firebase.getIdToken !== 'function') return undefined;
      const token = await Firebase.getIdToken();
      if (!token) return undefined;
      const res = await fetch('/api/feedback/server-context', { headers: { Authorization: 'Bearer ' + token } });
      if (!res.ok) return undefined;
      const data = await res.json();
      if (!data || !Array.isArray(data.errors) || data.errors.length === 0) return undefined;
      return JSON.stringify(data.errors);
    } catch (_) {
      return undefined;
    }
  }

  // ---------------- Screenshot attachments ----------------

  function revokeScreenshotUrls(targetState = state) {
    if (!targetState || !targetState.screenshots) return;
    targetState.screenshots.forEach((s) => { try { if (s.url) URL.revokeObjectURL(s.url); } catch (_) {} });
  }

  function setScreenshotError(msg) {
    if (!state || !state.screenshotError) return;
    state.screenshotError.textContent = msg || '';
    state.screenshotError.classList.toggle('feedback-upload-error-visible', !!msg);
  }

  function renderScreenshotPreviews() {
    if (!state || !state.screenshotPreview) return;
    const container = state.screenshotPreview;
    while (container.firstChild) container.removeChild(container.firstChild);
    state.screenshots.forEach((shot, index) => {
      container.appendChild(el('div', { className: 'feedback-upload-thumb' }, [
        el('img', { src: shot.url, alt: uiText('feedback.screenshotAlt', 'Attached image preview') }),
        el('button', {
          type: 'button',
          className: 'feedback-upload-remove',
          'aria-label': uiText('feedback.screenshotRemove', 'Remove image'),
          title: uiText('feedback.screenshotRemove', 'Remove image'),
          onClick: () => removeScreenshot(index),
        }, ['×']),
      ]));
    });
    container.setAttribute('aria-label', uiText(
      'feedback.screenshotCount',
      `Attached images: ${state.screenshots.length}`,
      { count: state.screenshots.length },
    ));
    if (state.screenshotAddBtn) {
      state.screenshotAddBtn.disabled = state.screenshots.length >= MAX_SCREENSHOT_COUNT;
    }
  }

  function removeScreenshot(index) {
    if (!state || !state.screenshots) return;
    const removed = state.screenshots.splice(index, 1)[0];
    if (removed && removed.url) { try { URL.revokeObjectURL(removed.url); } catch (_) {} }
    setScreenshotError('');
    renderScreenshotPreviews();
    refreshSubmitState();
  }

  function addScreenshotFiles(fileList) {
    if (!state) return;
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setScreenshotError('');
    for (const file of files) {
      if (state.screenshots.length >= MAX_SCREENSHOT_COUNT) {
        setScreenshotError(uiText('feedback.errorImageCount', 'You can attach up to 3 images.'));
        break;
      }
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setScreenshotError(uiText('feedback.errorImageType', 'Only PNG, JPEG, GIF, or WebP images are allowed.'));
        continue;
      }
      if (file.size > MAX_SCREENSHOT_BYTES) {
        setScreenshotError(uiText('feedback.errorImageTooLarge', 'Each image must be under 5 MB.'));
        continue;
      }
      state.screenshots.push({ file, url: URL.createObjectURL(file) });
    }
    renderScreenshotPreviews();
    refreshSubmitState();
  }

  // The Convex HTTP-action base (`*.convex.site`), derived from the deployment URL.
  function getConvexUploadEndpoint() {
    try {
      const base = (MoonfallCloud && typeof MoonfallCloud.deploymentUrl === 'function'
        && MoonfallCloud.deploymentUrl()) || window.CONVEX_URL || '';
      if (!base) return '';
      return base.replace('.convex.cloud', '.convex.site') + '/feedback/upload';
    } catch (_) { return ''; }
  }

  // Upload each attached image to the Convex HTTP action (bytes go to Convex's edge, NOT our
  // game server). The action stores + records the blob atomically, so no orphan can exist.
  // Throws on any failure so the caller can surface it and keep the modal open — never a fake success.
  async function uploadScreenshots(targetState = state) {
    const endpoint = getConvexUploadEndpoint();
    if (!endpoint) throw new Error('upload_unavailable');
    const token = (Firebase && typeof Firebase.getIdToken === 'function')
      ? await Firebase.getIdToken() : null;
    if (!token) throw new Error('not_authenticated');
    const keys = [];
    for (const shot of (targetState?.screenshots || [])) {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': shot.file.type },
        body: shot.file,
      });
      if (!res.ok) throw new Error('upload_failed');
      const data = await res.json().catch(() => null);
      const assetKey = data && data.assetKey;
      if (!assetKey) throw new Error('upload_failed');
      keys.push(assetKey);
    }
    return keys;
  }

  // ---------------- Builders ----------------

  function buildTypeChip(type, onChange) {
    const btn = el('button', {
      type: 'button',
      className: 'feedback-chip feedback-chip-type',
      'data-feedback-type': type.id,
      'aria-pressed': 'false',
      title: uiText(type.labelKey, type.id),
    }, [
      el('span', { className: 'feedback-chip-icon', textContent: type.icon }),
      el('span', { className: 'feedback-chip-label', textContent: uiText(type.labelKey, type.id) }),
    ]);
    btn.addEventListener('click', () => onChange(type.id));
    return btn;
  }

  function buildRatingChip(rating, onChange) {
    const btn = el('button', {
      type: 'button',
      className: 'feedback-chip feedback-chip-rating',
      'data-feedback-overall': String(rating.id),
      'aria-pressed': 'false',
      title: String(rating.id),
    }, [
      el('span', { className: 'feedback-chip-icon', textContent: rating.icon }),
    ]);
    btn.addEventListener('click', () => onChange(rating.id));
    return btn;
  }

  function buildFeatureRow(feature, onChange) {
    const row = el('div', { className: 'feedback-feature-row' });
    const featureLabel = uiText(feature.labelKey, feature.id);
    const ratingLabel = uiText(
      'feedback.featureRatingLabel',
      `Rating for ${featureLabel}`,
      { feature: featureLabel },
    );
    row.appendChild(el('span', { className: 'feedback-feature-label', textContent: featureLabel }));
    const scale = el('div', {
      className: 'feedback-feature-scale',
      role: 'group',
      'aria-label': ratingLabel,
    });
    for (let n = 1; n <= 5; n++) {
      const star = el('button', {
        type: 'button',
        className: 'feedback-feature-star',
        'data-feedback-feature': feature.id,
        'data-feedback-feature-value': String(n),
        'aria-pressed': 'false',
        'aria-label': `${ratingLabel}: ${n}/5`,
        title: String(n),
        textContent: '★',
      });
      star.addEventListener('click', () => onChange(feature.id, n));
      scale.appendChild(star);
    }
    row.appendChild(scale);
    return row;
  }

  function buildPriorityChip(priority, onChange) {
    const btn = el('button', {
      type: 'button',
      className: 'feedback-chip feedback-chip-priority',
      'data-feedback-priority': priority.id,
      'aria-pressed': 'false',
      title: uiText(priority.labelKey, priority.id),
      textContent: uiText(priority.labelKey, priority.id),
    });
    btn.addEventListener('click', () => onChange(priority.id));
    return btn;
  }

  function buildNpsChip(value, onChange) {
    const btn = el('button', {
      type: 'button',
      className: 'feedback-chip feedback-chip-nps',
      'data-feedback-nps': String(value),
      'aria-pressed': 'false',
      title: String(value),
      textContent: String(value),
    });
    btn.addEventListener('click', () => onChange(value));
    return btn;
  }

  // ---------------- State setters ----------------

  function refreshSubmitState() {
    if (!state || !state.submitBtn) return;
    const enabled = !!state.type && state.overallRating != null && !state.submitting;
    state.submitBtn.disabled = !enabled;
    if (state.charCount && state.commentArea) {
      const len = (state.commentArea.value || '').trim().length;
      state.charCount.textContent = uiText('feedback.charCount', `${len}/${MESSAGE_MAX_LENGTH}`, { count: len });
    }
  }

  function setType(typeId) {
    state.type = typeId;
    overlay.querySelectorAll('[data-feedback-type]').forEach((btn) => {
      const isActive = btn.getAttribute('data-feedback-type') === typeId;
      btn.classList.toggle('feedback-chip-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    refreshSubmitState();
  }

  function setOverallRating(value) {
    state.overallRating = state.overallRating === value ? null : value;
    overlay.querySelectorAll('[data-feedback-overall]').forEach((btn) => {
      const isActive = state.overallRating != null && btn.getAttribute('data-feedback-overall') === String(state.overallRating);
      btn.classList.toggle('feedback-chip-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    refreshSubmitState();
  }

  function setFeatureRating(feature, value) {
    // Clicking the same star value clears the rating (toggle off)
    if (value == null || state.featureRatings[feature] === value) {
      delete state.featureRatings[feature];
    } else {
      state.featureRatings[feature] = value;
    }
    overlay.querySelectorAll(`[data-feedback-feature="${feature}"]`).forEach((btn) => {
      const btnValue = Number(btn.getAttribute('data-feedback-feature-value'));
      const isActive = state.featureRatings[feature] != null && btnValue <= state.featureRatings[feature];
      const isSelected = state.featureRatings[feature] === btnValue;
      btn.classList.toggle('feedback-star-active', isActive);
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  function setPriority(priorityId) {
    state.priority = state.priority === priorityId ? null : priorityId;
    overlay.querySelectorAll('[data-feedback-priority]').forEach((btn) => {
      const isActive = btn.getAttribute('data-feedback-priority') === state.priority;
      btn.classList.toggle('feedback-chip-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function setNps(value) {
    state.nps = state.nps === value ? null : value;
    overlay.querySelectorAll('[data-feedback-nps]').forEach((btn) => {
      const isActive = state.nps != null && btn.getAttribute('data-feedback-nps') === String(state.nps);
      btn.classList.toggle('feedback-chip-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  // ---------------- Common modal plumbing ----------------

  function showToast(message) {
    try {
      if (Lobby && typeof Lobby.showToast === 'function') {
        Lobby.showToast(message);
        return;
      }
    } catch (_) {}
    const toast = document.createElement('div');
    toast.className = 'lobby-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.classList.add('lobby-toast-exit'), 2400);
    setTimeout(() => { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 3000);
  }

  function attachKeyHandler() {
    onKeyDown = function (e) {
      if (!overlay) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        attemptClose();
      } else if (e.key === 'Tab') {
        const focusable = Array.from(overlay.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
          .filter((node) => !node.disabled && node.offsetParent !== null);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
  }

  function detachKeyHandler() {
    if (onKeyDown) {
      document.removeEventListener('keydown', onKeyDown, true);
      onKeyDown = null;
    }
  }

  function hasUnsavedInput(targetState = state) {
    if (!targetState) return false;
    if (targetState.type) return true;
    if (targetState.overallRating != null) return true;
    if (targetState.nps != null) return true;
    if (targetState.priority) return true;
    if (targetState.featureRatings && Object.keys(targetState.featureRatings).length > 0) return true;
    if (targetState.commentArea && targetState.commentArea.value.trim().length > 0) return true;
    if (targetState.screenshots && targetState.screenshots.length > 0) return true;
    return false;
  }

  async function attemptClose(force, { restoreFocus = true } = {}) {
    const ownerOverlay = overlay;
    const ownerState = state;
    if (!ownerOverlay || !ownerState) return false;
    if (!force && hasUnsavedInput(ownerState)) {
      const message = uiText('feedback.unsavedConfirm', 'Discard your feedback?');
      pendingCloseOwner = ownerOverlay;
      let confirmed = false;
      try {
        confirmed = await Confirm.ask(message, { destructive: true });
      } finally {
        if (pendingCloseOwner === ownerOverlay) pendingCloseOwner = null;
      }
      if (!confirmed || overlay !== ownerOverlay || state !== ownerState) return false;
    }
    return closeNow({ restoreFocus, expectedOverlay: ownerOverlay });
  }

  function closeNow({ restoreFocus = true, expectedOverlay = overlay } = {}) {
    if (!expectedOverlay || overlay !== expectedOverlay) return false;
    const ownerState = state;
    if (pendingCloseOwner === expectedOverlay) {
      pendingCloseOwner = null;
      Confirm?.dismiss?.({ restoreFocus: false });
    }
    if (focusTimer) {
      clearTimeout(focusTimer);
      focusTimer = null;
    }
    detachKeyHandler();
    ownerState?.detachScrollCue?.();
    revokeScreenshotUrls(ownerState); // free preview object URLs before dropping state (no blob leak)
    if (expectedOverlay.parentNode) expectedOverlay.parentNode.removeChild(expectedOverlay);
    overlay = null;
    state = null;
    surfaceGeneration += 1;
    releaseLobbySurface();
    if (restoreFocus && pendingReturnFocus && typeof pendingReturnFocus.focus === 'function') {
      try { pendingReturnFocus.focus(); } catch (_) {}
    }
    pendingReturnFocus = null;
    return true;
  }

  async function handleSubmit(ev) {
    if (ev && ev.preventDefault) ev.preventDefault();
    if (!state || state.submitting) return;
    if (!state.type || state.overallRating == null) return;
    const ownerOverlay = overlay;
    const ownerState = state;
    const generation = surfaceGeneration;
    const ownerIsCurrent = () => (
      overlay === ownerOverlay
      && state === ownerState
      && surfaceGeneration === generation
    );

    const trimmedComment = ownerState.commentArea ? ownerState.commentArea.value.trim() : '';
    if (trimmedComment.length > MESSAGE_MAX_LENGTH) {
      ownerState.errorEl.textContent = uiText('feedback.errorTooLong', `Please keep the comment under ${MESSAGE_MAX_LENGTH} characters.`);
      ownerState.errorEl.classList.add('feedback-error-visible');
      return;
    }

    ownerState.submitting = true;
    refreshSubmitState();
    ownerState.errorEl.classList.remove('feedback-error-visible');

    // Upload any attached screenshots FIRST. On failure, surface it and keep the modal
    // open — we never silently drop an image the user chose to attach.
    let screenshotKeys;
    try {
      if (ownerState.screenshots && ownerState.screenshots.length > 0) {
        if (!MoonfallCloud || typeof MoonfallCloud.mutation !== 'function') {
          throw new Error('cloud_unavailable');
        }
        screenshotKeys = await uploadScreenshots(ownerState);
        if (!ownerIsCurrent()) return;
      }
    } catch (err) {
      if (!ownerIsCurrent()) return;
      console.error('[Feedback] screenshot upload failed', err);
      ownerState.errorEl.textContent = uiText('feedback.errorUploadFailed', 'Could not upload your image. Please try again or remove it.');
      ownerState.errorEl.classList.add('feedback-error-visible');
      ownerState.submitting = false;
      refreshSubmitState();
      return;
    }

    const recentServerErrors = await getRecentServerErrors();
    if (!ownerIsCurrent()) return;

    const featureRatings = Object.keys(ownerState.featureRatings).length > 0 ? ownerState.featureRatings : undefined;

    const payload = {
      type: ownerState.type,
      overallRating: ownerState.overallRating,
      featureRatings,
      priority: ownerState.priority || undefined,
      nps: ownerState.nps == null ? undefined : ownerState.nps,
      message: trimmedComment || undefined,
      okToFollowUp: !!(ownerState.followUpCheckbox && ownerState.followUpCheckbox.checked),
      triggerSource: ownerState.triggerSource,
      pageRoute: getPageRoute(),
      pageUrl: (location && location.href) || undefined,
      viewport: getViewport(),
      userAgent: (navigator && navigator.userAgent) || undefined,
      language: getLanguage(),
      clientBuildHash: window.MOONFALL_BUILD_HASH || undefined,
      recentConsoleErrors: getConsoleErrorsBlob(),
      recentServerErrors,
      screenshotKeys: (screenshotKeys && screenshotKeys.length > 0) ? screenshotKeys : undefined,
      gameContext: ownerState.gameContext,
    };

    try {
      if (!MoonfallCloud || typeof MoonfallCloud.mutation !== 'function') {
        throw new Error('cloud_unavailable');
      }
      const result = await MoonfallCloud.mutation('feedback:submitFeedback', payload);
      if (!ownerIsCurrent()) return;
      closeNow({ expectedOverlay: ownerOverlay });
      showToast(uiText('feedback.successToast', 'Thank you — your feedback was received.'));
      // Forest Whisper, celebrated in the session that earned it. The server
      // already told us whether anything was unlocked, so only the first
      // submission ever spends the acknowledgement call.
      if (result?.cardbackUnlocked) void getCloudAchievementBannerConsumer()?.();
    } catch (err) {
      if (!ownerIsCurrent()) return;
      console.error('[Feedback] submit failed', err);
      const message = String((err && err.message) || '');
      let userMessage;
      if (/too many submissions/i.test(message)) {
        userMessage = uiText('feedback.errorRateLimit', 'Too many submissions. Please wait a bit.');
      } else if (/not authenticated/i.test(message)) {
        userMessage = uiText('feedback.errorNotSignedIn', 'Please sign in to send feedback.');
      } else {
        userMessage = uiText('feedback.errorGeneric', 'Could not send feedback. Please try again.');
      }
      ownerState.errorEl.textContent = userMessage;
      ownerState.errorEl.classList.add('feedback-error-visible');
      ownerState.submitting = false;
      refreshSubmitState();
    }
  }

  // ---------------- Open ----------------

  /*
   * `game-active` is set when a match starts and removed only on the way back
   * to the lobby, so it also covers the terminal result popup. Feedback is
   * lobby chrome: it has no door on the battle surface (the result screen's
   * "Share feedback" button was retired to thin its button row) and no
   * business floating over one. The predicate is a BODY-CLASS one, not
   * `opts.triggerSource`, because the trigger source is caller-supplied and
   * unverified, and because the CSS half of this rule (feedback.css) can only
   * see a selector — whatever gates here has to be expressible there or the
   * two layers drift apart and one of them silently wins.
   */
  function isBattleSurfaceExclusive() {
    const body = document.body;
    if (!body) return false;
    return body.classList.contains('game-transition-pending')
      || body.classList.contains('game-active');
  }

  function open(options) {
    if (isBattleSurfaceExclusive()) return false;
    if (overlay || !claimLobbySurface()) return false;
    const opts = options || {};
    const generation = ++surfaceGeneration;
    if (focusTimer) {
      clearTimeout(focusTimer);
      focusTimer = null;
    }

    pendingReturnFocus = document.activeElement || null;

    state = {
      type: null,
      overallRating: null,
      featureRatings: {},
      priority: null,
      nps: null,
      triggerSource: opts.triggerSource || 'lobby',
      gameContext: opts.gameContext || undefined,
      screenshots: [],
      submitting: false,
    };

    overlay = el('div', {
      className: 'feedback-modal',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'feedback-title',
      'aria-describedby': 'feedback-subtitle',
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) attemptClose();
    });

    const content = el('div', { className: 'feedback-content' });

    // --- Header (sticky top) ---
    const header = el('div', { className: 'feedback-header' });
    const closeBtn = el('button', {
      type: 'button',
      className: 'feedback-close',
      'aria-label': uiText('feedback.cancel', 'Cancel'),
      title: uiText('feedback.cancel', 'Cancel'),
      onClick: () => attemptClose(),
    }, ['×']);
    header.appendChild(closeBtn);
    header.appendChild(el('h2', { id: 'feedback-title', className: 'feedback-title', textContent: uiText('feedback.modalTitle', 'Share your feedback') }));
    header.appendChild(buildSubtitle());
    content.appendChild(header);

    // --- Form body (3-column grid) ---
    const form = el('form', { className: 'feedback-form', onSubmit: handleSubmit });

    function buildSection(name, headerOpts, children) {
      const labelId = `feedback-section-${name}-label`;
      const section = el('div', {
        className: `feedback-section feedback-section-${name}`,
        role: 'group',
        'aria-labelledby': labelId,
      });
      const sectionHeader = el('div', {
        id: labelId,
        className: 'feedback-label' + (headerOpts.required ? ' feedback-label-required' : ''),
        textContent: headerOpts.label,
      });
      section.appendChild(sectionHeader);
      if (headerOpts.hint) {
        section.appendChild(el('p', { className: 'feedback-section-hint', textContent: headerOpts.hint }));
      }
      (children || []).forEach((c) => section.appendChild(c));
      return section;
    }

    const body = el('div', { className: 'feedback-body' });
    const grid = el('div', { className: 'feedback-grid' });

    // --- Column 1: Type + Overall vibe + NPS ---
    const colLeft = el('div', { className: 'feedback-col feedback-col-left' });

    const typeRow = el('div', { className: 'feedback-chip-row feedback-chip-row-type' });
    TYPES.forEach((type) => typeRow.appendChild(buildTypeChip(type, setType)));
    colLeft.appendChild(buildSection('type', {
      label: uiText('feedback.typeLabel', 'What kind of feedback?'),
      required: true,
    }, [typeRow]));

    const ratingRow = el('div', { className: 'feedback-chip-row feedback-chip-row-rating' });
    OVERALL_RATINGS.forEach((rating) => ratingRow.appendChild(buildRatingChip(rating, setOverallRating)));
    colLeft.appendChild(buildSection('overall', {
      label: uiText('feedback.overallVibeLabel', 'Overall vibe right now?'),
      required: true,
    }, [ratingRow]));

    const npsRow = el('div', { className: 'feedback-chip-row feedback-chip-row-nps' });
    for (let i = 0; i <= 10; i++) npsRow.appendChild(buildNpsChip(i, setNps));
    const npsScale = el('div', { className: 'feedback-nps-scale' }, [
      el('span', { textContent: uiText('feedback.npsLow', 'Not at all') }),
      el('span', { textContent: uiText('feedback.npsHigh', 'Definitely') }),
    ]);
    colLeft.appendChild(buildSection('nps', {
      label: uiText('feedback.npsLabel', 'How likely are you to recommend Moonfall to a friend?'),
    }, [npsRow, npsScale]));

    grid.appendChild(colLeft);

    // --- Column 2: Feature ratings (the tall one) ---
    const colMid = el('div', { className: 'feedback-col feedback-col-mid' });
    const featuresGrid = el('div', { className: 'feedback-feature-grid' });
    FEATURES.forEach((f) => featuresGrid.appendChild(buildFeatureRow(f, setFeatureRating)));
    colMid.appendChild(buildSection('features', {
      label: uiText('feedback.featureRatingsLabel', 'Rate the parts you have an opinion on'),
    }, [featuresGrid]));
    grid.appendChild(colMid);

    // --- Column 3: Priority + Comment + Follow-up ---
    const colRight = el('div', { className: 'feedback-col feedback-col-right' });

    const priorityRow = el('div', { className: 'feedback-chip-row feedback-chip-row-priority' });
    PRIORITIES.forEach((p) => priorityRow.appendChild(buildPriorityChip(p, setPriority)));
    colRight.appendChild(buildSection('priority', {
      label: uiText('feedback.priorityLabel', "If we could only fix ONE thing first, which?"),
    }, [priorityRow]));

    const commentArea = el('textarea', {
      id: 'feedback-comment',
      className: 'feedback-textarea',
      'aria-labelledby': 'feedback-section-comment-label',
      maxlength: String(MESSAGE_MAX_LENGTH),
      placeholder: uiText('feedback.commentPlaceholder', 'Optional — long thoughts welcome on Discord.'),
    });
    commentArea.addEventListener('input', refreshSubmitState);
    state.commentArea = commentArea;
    const charCount = el('div', { className: 'feedback-char-count' });
    state.charCount = charCount;
    const followUpRow = el('label', { className: 'feedback-followup' });
    const followUpCheckbox = el('input', { type: 'checkbox', id: 'feedback-followup' });
    state.followUpCheckbox = followUpCheckbox;
    followUpRow.appendChild(followUpCheckbox);
    followUpRow.appendChild(el('span', { textContent: uiText('feedback.followUpLabel', 'OK to email me if I have a follow-up question') }));
    colRight.appendChild(buildSection('comment', {
      label: uiText('feedback.commentLabel', 'Anything specific? (optional)'),
    }, [commentArea, charCount, followUpRow]));

    // --- Screenshot attachments (manual upload — users pick the image themselves) ---
    const uploadInput = el('input', {
      type: 'file',
      className: 'feedback-upload-input',
      accept: ALLOWED_IMAGE_TYPES.join(','),
      multiple: true,
      onChange: (e) => { addScreenshotFiles(e.target.files); e.target.value = ''; },
    });
    uploadInput.style.display = 'none';
    const uploadAddBtn = el('button', {
      type: 'button',
      className: 'feedback-upload-add',
      onClick: () => uploadInput.click(),
    }, ['📎 ', uiText('feedback.screenshotAdd', 'Attach image')]);
    state.screenshotAddBtn = uploadAddBtn;
    const uploadPreview = el('div', {
      className: 'feedback-upload-preview',
      role: 'group',
      'aria-label': uiText('feedback.screenshotCount', 'Attached images: {{count}}', { count: 0 }),
    });
    state.screenshotPreview = uploadPreview;
    const uploadError = el('div', { className: 'feedback-upload-error', role: 'alert', 'aria-live': 'polite' });
    state.screenshotError = uploadError;
    colRight.appendChild(buildSection('screenshot', {
      label: uiText('feedback.screenshotLabel', 'Attach a screenshot (optional)'),
      hint: uiText('feedback.screenshotHint', 'Up to 3 images (PNG, JPEG, GIF, or WebP · max 5 MB each).'),
    }, [uploadAddBtn, uploadInput, uploadPreview, uploadError]));

    grid.appendChild(colRight);

    body.appendChild(grid);
    const scrollCue = el('div', {
      className: 'feedback-scroll-cue',
      'aria-hidden': 'true',
      hidden: true,
    }, [
      el('span', {
        className: 'feedback-scroll-cue-label',
        textContent: uiText('feedback.scrollForMore', 'Scroll for more'),
      }),
      el('span', { className: 'feedback-scroll-cue-icon', textContent: '⌄' }),
    ]);
    const scrollFrame = el('div', {
      className: 'feedback-scroll-frame',
    }, [body]);
    const refreshScrollCue = () => {
      // The cue lives beside the footer actions, so showing it cannot shrink
      // this viewport or manufacture its own overflow. Measure the native
      // scroll owner directly and only toggle the inline affordance.
      const canScroll = body.scrollHeight > body.clientHeight + 4;
      const atBottom = !canScroll
        || body.scrollTop + body.clientHeight >= body.scrollHeight - 4;
      body.dataset.scrollable = String(canScroll);
      body.dataset.atBottom = String(atBottom);
      scrollCue.hidden = !canScroll || atBottom;
    };
    body.addEventListener('scroll', refreshScrollCue, { passive: true });
    window.addEventListener('resize', refreshScrollCue);
    let resizeObserver = null;
    let mutationObserver = null;
    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(refreshScrollCue);
      resizeObserver.observe(body);
      resizeObserver.observe(scrollFrame);
      resizeObserver.observe(grid);
    }
    if (typeof MutationObserver === 'function') {
      // A ResizeObserver on the scroll viewport alone does not fire when its
      // border box stays fixed but attachments, errors, or translated copy make
      // the content taller. Watch content mutations in every engine as well.
      mutationObserver = new MutationObserver(refreshScrollCue);
      mutationObserver.observe(body, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'hidden', 'src'],
      });
    }
    state.detachScrollCue = () => {
      body.removeEventListener('scroll', refreshScrollCue);
      window.removeEventListener('resize', refreshScrollCue);
      resizeObserver?.disconnect?.();
      mutationObserver?.disconnect?.();
    };
    state.refreshScrollCue = refreshScrollCue;
    form.appendChild(scrollFrame);

    // --- Footer (sticky bottom): error + buttons ---
    const footer = el('div', { className: 'feedback-footer' });

    const errorEl = el('div', { className: 'feedback-error', role: 'alert', 'aria-live': 'polite' });
    state.errorEl = errorEl;
    footer.appendChild(errorEl);

    const buttonRow = el('div', { className: 'feedback-button-row' });
    const cancelBtn = el('button', {
      type: 'button',
      className: 'feedback-btn feedback-btn-secondary',
      textContent: uiText('feedback.cancel', 'Cancel'),
      onClick: () => attemptClose(),
    });
    const submitBtn = el('button', {
      type: 'submit',
      className: 'feedback-btn feedback-btn-primary',
      disabled: true,
      textContent: uiText('feedback.submit', 'Send feedback'),
    });
    state.submitBtn = submitBtn;
    buttonRow.appendChild(cancelBtn);
    buttonRow.appendChild(scrollCue);
    buttonRow.appendChild(submitBtn);
    footer.appendChild(buttonRow);

    form.appendChild(footer);

    content.appendChild(form);
    overlay.appendChild(content);
    document.body.appendChild(overlay);
    const ownerOverlay = overlay;
    const ownerState = state;

    attachKeyHandler();
    refreshSubmitState();

    focusTimer = setTimeout(() => {
      focusTimer = null;
      try {
        if (
          overlay !== ownerOverlay
          || state !== ownerState
          || surfaceGeneration !== generation
        ) return;
        ownerState.refreshScrollCue?.();
        const firstChip = ownerOverlay.querySelector('[data-feedback-type]');
        if (firstChip) firstChip.focus();
      } catch (_) {}
    }, 30);
    return true;
  }

  function attachLobbyButton() {
    const btn = document.getElementById('feedback-link');
    if (!btn || btn.dataset.feedbackBound === '1') return;
    btn.dataset.feedbackBound = '1';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      open({ triggerSource: 'lobby' });
    });
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attachLobbyButton, { once: true });
    } else {
      attachLobbyButton();
    }
  }

  return {
    open,
    close: (options) => attemptClose(true, options),
    isOpen,
    attachLobbyButton,
    _internals: { TYPES, OVERALL_RATINGS, FEATURES, PRIORITIES, MESSAGE_MAX_LENGTH },
  };
})();

export { Feedback };
